import React, { useMemo, forwardRef } from 'react';
import cn from 'classnames';

import Card from '../Card';
import Text from '../Text';
import BookCover from '../BookCover';
import Image from '../Image';
import Share from '../Share';

import * as styles from './Mix.module.css';
import * as textStyles from '../../styles/text.module.css';
import { externalLink } from '../../util';
import { useElementDimensions } from '../../util/hooks';
import { isLightColor } from '../../util/functions';

const MixBlock = forwardRef(
  ({ label, labelBackground, headline, text, cta, url, className }, ref) => {
    return (
      <div className={cn(styles.block, className)} ref={ref}>
        <div
          className={cn(styles.label)}
          style={{
            backgroundColor: labelBackground,
            color: isLightColor(labelBackground) ? '#000000' : '#ffffff'
          }}
        >
          {label}
        </div>
        <h4 className={cn(styles.blockHeadline)}>{headline}</h4>
        <Text className={cn(styles.blockText)} text={text} />
        {url && (
          <a className={styles.blockCta} {...externalLink} href={url}>
            {cta} →
          </a>
        )}
      </div>
    );
  }
);

const Mix = ({
  headline,
  image,
  imagePosition,
  byLine,
  intro,
  titleRight,
  highlightBlock,
  highlightBlockImage,
  blocks,
  id,
  slug
}) => {
  /* eslint-disable no-unused-vars */
  const [highlightRef, [_, highlightHeight]] = useElementDimensions();
  const [blockRef, [__, blockHeight]] = useElementDimensions();
  const [titleRef, [___, titleHeight]] = useElementDimensions();
  /* eslint-enable no-unused-vars */

  const maxImageHeight = useMemo(() => {
    const highlightCardDelta = blockHeight - highlightHeight;

    if (!titleRight) return highlightCardDelta - titleHeight;
    return highlightCardDelta;
  }, [highlightHeight, blockHeight, titleHeight, titleRight]);

  return (
    <Card
      id={id}
      title="Un mix"
      className={cn(styles.root, {
        [styles.titleLeft]: !titleRight
      })}
      animate
      ref={blockRef}
    >
      <div
        className={cn(styles.introSection, {
          [styles.introSectionRight]: titleRight
        })}
        ref={titleRef}
      >
        <h3 className={cn(textStyles.headline, styles.headline)}>{headline}</h3>
        <div className={cn(styles.byLine)}>
          <p className={styles.name}>Por {byLine.name}</p>
          {(byLine.role || byLine.company) && (
            <p>
              {byLine.role}
              {byLine.role && byLine.company && ', '}
              {byLine.company}
            </p>
          )}
        </div>
        {intro && (
          <Text
            className={cn(textStyles.intro, styles.introText)}
            text={intro}
          />
        )}
      </div>

      <div
        className={cn(styles.lowerSection)}
        style={{
          '--max-image-height': `${maxImageHeight}px`,
          '--image-vertical-position': imagePosition
        }}
      >
        <div className={cn(styles.highlightBlock)} ref={highlightRef}>
          <Image image={image} className={cn(styles.introImage)} />
          {highlightBlock.image && (
            <BookCover
              noShadow={!highlightBlock.shadow}
              coverImage={highlightBlockImage}
              bookName={highlightBlock.title}
              className={cn(styles.highlightBlockImage)}
            />
          )}
          <MixBlock
            className={cn({
              [styles.highlightBlockWithImage]: !!highlightBlock.image
            })}
            label={highlightBlock.label}
            labelBackground={highlightBlock.labelBackground}
            headline={highlightBlock.headline}
            text={highlightBlock.text}
            cta={highlightBlock.cta}
            url={highlightBlock.url}
          />
        </div>
        <div className={cn(styles.text)}>
          {blocks.map((block, i) => (
            <MixBlock key={i} {...block} />
          ))}
        </div>
      </div>
      <Share
        url={`${slug}#${id}`}
        className={cn(styles.share, {
          [styles.shareRight]: !titleRight
        })}
      />
    </Card>
  );
};

export default Mix;
